import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { DecimalPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ProspectingListConfig,
  ProspectingPreview,
  ProspectingService,
} from '../../../services/prospecting.service';
import { IcpService } from '../../../services/icp.service';

export const JOB_TITLE_PRESETS = [
  'CEO', 'Founder', 'CTO', 'CFO', 'COO', 'VP Sales', 'VP Marketing', 'VP Engineering',
  'Director', 'Manager', 'Head of Department',
];

export const DEPARTMENT_PRESETS = [
  'Leadership', 'Engineering', 'Sales', 'Marketing', 'Finance/Accounting', 'HR', 'Operations',
  'Legal', 'Product', 'Customer Success', 'IT', 'Design', 'Procurement',
];

/** Browser UUID with a fallback for non-secure contexts (http://localhost-like hosts). */
export function newListId(): string {
  const c: any = typeof crypto !== 'undefined' ? crypto : null;
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface MappedGroup {
  label: string;
  values: string[];
}

/**
 * "Proceed to Leads" dialog on the Advanced Prospecting companies page:
 * shows how the search maps to lead targeting, lets the user pick the people
 * (job titles / departments) to find at those companies, then opens the
 * Leads page in prospecting-list mode.
 */
@Component({
  selector: 'app-proceed-to-leads-modal',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, DecimalPipe, FormsModule],
  templateUrl: './proceed-to-leads-modal.component.html',
})
export class ProceedToLeadsModalComponent implements OnChanges, OnDestroy {
  @Input() isVisible = false;
  @Input() filters: any[] = [];
  @Input() totalRecords = 0;
  @Input() summaryLabel = '';
  @Output() modalClosed = new EventEmitter<void>();

  readonly titlePresets = JOB_TITLE_PRESETS;
  readonly departmentPresets = DEPARTMENT_PRESETS;

  preview: ProspectingPreview | null = null;
  isLoadingPreview = false;
  previewError = false;

  jobTitles: string[] = [];
  departments: string[] = [];
  titleInput = '';
  departmentInput = '';

  isContinuing = false;
  showValidation = false;

  private prefilledFromIcp = false;
  private destroy$ = new Subject<void>();
  private previewCancel$ = new Subject<void>();

  constructor(
    private prospectingService: ProspectingService,
    private icpService: IcpService,
    private router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && this.isVisible) {
      this.isContinuing = false;
      this.showValidation = false;
      this.loadPreview();
      this.prefillFromIcp();
    }
  }

  ngOnDestroy(): void {
    this.previewCancel$.next();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Preview ───────────────────────────────────────────────────────

  loadPreview(): void {
    this.previewCancel$.next();
    this.isLoadingPreview = true;
    this.previewError = false;
    this.preview = null;

    this.prospectingService.preview(this.filters ?? [])
      .pipe(takeUntil(this.previewCancel$), takeUntil(this.destroy$))
      .subscribe({
        next: (preview) => {
          this.preview = preview;
          this.isLoadingPreview = false;
        },
        error: () => {
          this.isLoadingPreview = false;
          this.previewError = true;
        }
      });
  }

  get mappedGroups(): MappedGroup[] {
    const mapped = this.preview?.mapping?.mapped;
    if (!mapped) {
      return [];
    }
    const groups: MappedGroup[] = [
      { label: 'Countries', values: mapped.countries ?? [] },
      { label: 'Industries', values: mapped.industries ?? [] },
      { label: 'Company sizes', values: mapped.company_sizes ?? [] },
      { label: 'Company revenues', values: mapped.company_revenues ?? [] },
      { label: 'Business categories', values: mapped.business_categories ?? [] },
      { label: 'Company products', values: mapped.company_products ?? [] },
    ];
    return groups.filter(group => group.values.length > 0);
  }

  get listOnly(): { label: string; value: string }[] {
    return this.preview?.mapping?.list_only ?? [];
  }

  get warnings(): string[] {
    return this.preview?.mapping?.warnings ?? [];
  }

  // ─── ICP prefill ───────────────────────────────────────────────────

  private prefillFromIcp(): void {
    if (this.prefilledFromIcp || this.jobTitles.length || this.departments.length) {
      return;
    }
    this.prefilledFromIcp = true;

    this.icpService.getIcps({ per_page: 100 })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ data }) => {
          const icp = (data ?? []).find(item => item.is_default);
          if (!icp || this.jobTitles.length || this.departments.length) {
            return;
          }
          this.jobTitles = this.splitList(icp.main_buyer_job_title);
          this.departments = this.splitList(icp.main_buyer_department);
        },
        error: () => { /* prefill is a convenience only */ }
      });
  }

  private splitList(value: string | null | undefined): string[] {
    const seen = new Set<string>();
    return (value ?? '')
      .split(',')
      .map(part => part.trim())
      .filter(part => {
        const key = part.toLowerCase();
        if (!part || key === 'other' || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .slice(0, 20);
  }

  // ─── Chips ─────────────────────────────────────────────────────────

  hasValue(list: string[], value: string): boolean {
    const key = value.toLowerCase();
    return list.some(item => item.toLowerCase() === key);
  }

  togglePreset(kind: 'title' | 'department', value: string): void {
    const list = kind === 'title' ? this.jobTitles : this.departments;
    const next = this.hasValue(list, value)
      ? list.filter(item => item.toLowerCase() !== value.toLowerCase())
      : [...list, value];
    this.assign(kind, next);
  }

  addFromInput(kind: 'title' | 'department'): void {
    const raw = kind === 'title' ? this.titleInput : this.departmentInput;
    let list = kind === 'title' ? [...this.jobTitles] : [...this.departments];
    for (const term of this.splitList(raw)) {
      if (!this.hasValue(list, term)) {
        list = [...list, term];
      }
    }
    this.assign(kind, list.slice(0, 20));
    if (kind === 'title') {
      this.titleInput = '';
    } else {
      this.departmentInput = '';
    }
  }

  remove(kind: 'title' | 'department', value: string): void {
    const list = kind === 'title' ? this.jobTitles : this.departments;
    this.assign(kind, list.filter(item => item !== value));
  }

  private assign(kind: 'title' | 'department', list: string[]): void {
    if (kind === 'title') {
      this.jobTitles = list;
    } else {
      this.departments = list;
    }
  }

  /** Free-text the user typed but didn't press Enter on yet. */
  private flushInputs(): void {
    if (this.titleInput.trim()) {
      this.addFromInput('title');
    }
    if (this.departmentInput.trim()) {
      this.addFromInput('department');
    }
  }

  get hasPeopleTargets(): boolean {
    return this.jobTitles.length + this.departments.length > 0;
  }

  // ─── Actions ───────────────────────────────────────────────────────

  close(): void {
    if (this.isContinuing) {
      return;
    }
    this.modalClosed.emit();
  }

  proceed(): void {
    if (this.isContinuing) {
      return;
    }
    this.flushInputs();
    if (!this.hasPeopleTargets) {
      this.showValidation = true;
      return;
    }

    this.isContinuing = true;
    const listId = newListId();
    const config: ProspectingListConfig = {
      filters: this.filters ?? [],
      job_titles: [...this.jobTitles],
      departments: [...this.departments],
      summary_label: this.summaryLabel || 'Advanced Prospecting search',
      total_companies: this.totalRecords || 0,
    };
    ProspectingService.storeConfig(listId, config);

    this.router.navigate(['/leads'], { queryParams: { list: listId } })
      .then(ok => {
        this.isContinuing = false;
        if (ok) {
          this.modalClosed.emit();
        }
      })
      .catch(() => {
        this.isContinuing = false;
      });
  }
}
